import type { AnySceneObjectT } from "tweenly"
import CloseIcon from "@mui/icons-material/Close"
import FolderOffIcon from "@mui/icons-material/FolderOff"
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Typography,
    useTheme,
} from "@mui/material"
import { t } from "i18next"
import { FILTER_TYPE, FilterType } from "../../../constants/filterLists"
import { defaultSequence } from "../../../data/defaults/objects.types.defaults"
import { createChildObject } from "../../../helpers/object.helpers"
import { dateToString } from "../../../helpers/date.helpers"
import { getGraphicImageUrl } from "../../../helpers/graphic.helpers"
import { selectUndoable } from "../../../helpers/selector.helpers"
import { getFullNameString } from "../../../helpers/string.helpers"
import { useAnimationActions } from "../../../hooks/useAnimationActions"
import { useCurrentCompanyStats } from "../../../hooks/useCompanyStats"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppDispatch, useAppStore } from "../../../hooks/useRedux"
import {
    addSelectedAnimationsAction,
    clearSelectedAnimationsAction,
} from "../../../store/slices/activeAnimation.slice"
import {
    clearAllSelectedObjectsAction,
    setActiveObjectAction,
} from "../../../store/slices/activeObject.slice"
import { AssetT, createAssetsFulfilled } from "../../../store/slices/assets.slice"
import { selectObjects } from "../../../store/slices/objects.slice"
import { Transition } from "../../common/Transition"

interface Props {
    open: boolean
    onClose: () => void
    parentId: string | null
    object?: AnySceneObjectT
    type: FilterType
}

const AssetsDialog = ({ open, onClose, parentId, object, type }: Props) => {
    const { readImageFileFromURL, replaceImageSourceFromUrl } = useObjectActions()
    const { createSequenceAnimations, buildSequenceAnimations } = useAnimationActions()
    const dispatch = useAppDispatch()
    // read on demand, not subscribed: this dialog is mounted in every objects tree node, so a
    // subscription to the objects list re-rendered all of them on every object edit
    const store = useAppStore()

    const { assets } = useCurrentCompanyStats()
    const theme = useTheme()

    const handleClick = async (asset: AssetT) => {
        if (asset.type === "image") {
            if (object) {
                replaceImageSourceFromUrl(asset.url!, object)
            } else {
                readImageFileFromURL(asset.url!, parentId, asset.slug)
            }
        } else if (asset.type === "sequence") {
            const objects = selectObjects(selectUndoable(store.getState()).objects)
            const item = createChildObject(objects, defaultSequence, parentId, asset.slug)

            if (!item) return

            const { animations, urls } = await buildSequenceAnimations(asset, item.id)
            const img = new Image()
            img.addEventListener("load", function () {
                createSequenceAnimations(animations, item, { width: img.width, height: img.height })
            })
            img.src = urls[0]
            const animationIds: string[] = animations.map((animation) => animation.id)
            dispatch(clearSelectedAnimationsAction())
            dispatch(clearAllSelectedObjectsAction())
            dispatch(setActiveObjectAction({ id: item.id }))
            dispatch(addSelectedAnimationsAction(animationIds))
        }

        dispatch(createAssetsFulfilled([asset]))
        onClose()
    }

    const Icon = FILTER_TYPE.find((f) => f.name === type)?.icon

    const filteredAssets = assets.filter((asset) => asset.type && type?.includes(asset.type))

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            keepMounted
            sx={{ zIndex: 1400 }}
            maxWidth={"xl"}
        >
            <Box m={3}>
                <DialogTitle component={"h4"} sx={{ display: "flex", alignItems: "center", pb: 2 }}>
                    {Icon && <Icon sx={{ mr: 1 }} fontSize="large" />}
                    {t("editor:assetsDialog.title")}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 32,
                        top: 32,
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent>
                    {filteredAssets.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                                px: 4,
                                minHeight: 300,
                            }}
                        >
                            <FolderOffIcon
                                sx={{
                                    fontSize: 64,
                                    color: theme.palette.grey[400],
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme.palette.grey[200],
                                    mb: 1,
                                    fontWeight: 500,
                                }}
                            >
                                {t("editor:assetsDialog.emptyState.title")}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.grey[400],
                                    textAlign: "center",
                                    maxWidth: 400,
                                }}
                            >
                                {t("editor:assetsDialog.emptyState.description")}
                            </Typography>
                        </Box>
                    ) : (
                        <ImageList cols={6}>
                            {filteredAssets.map((asset) => (
                                <ImageListItem key={asset._id} onClick={() => handleClick(asset)}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            backgroundColor: theme.palette.grey[900],
                                            width: 160,
                                            height: 160,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={getGraphicImageUrl(asset)}
                                            sx={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                opacity: 0.7,
                                            }}
                                        />
                                    </Box>
                                    <ImageListItemBar
                                        title={
                                            asset.name.length > 20
                                                ? `${asset.name.substring(0, 16)}...`
                                                : asset.name
                                        }
                                        subtitle={
                                            <span>
                                                {asset.updated && dateToString(asset.updated)}
                                                {" | "}
                                                {!asset.user?.firstName && !asset.user?.lastName
                                                    ? asset.user?.email
                                                    : getFullNameString(
                                                          asset.user?.firstName,
                                                          asset.user?.lastName
                                                      )}
                                            </span>
                                        }
                                        position="below"
                                    />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    )}
                </DialogContent>
            </Box>
        </Dialog>
    )
}

export default AssetsDialog
